import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../lib/api-client';
import type {
  AnnouncementCategory,
  AnnouncementPriority,
  AnnouncementTarget,
  CreateAnnouncementInput,
  UpdateAnnouncementInput,
} from '@lms/shared/types';

export function useAnnouncements(params?: {
  page?: number;
  limit?: number;
  search?: string;
  category?: AnnouncementCategory;
  priority?: AnnouncementPriority;
  targetAudience?: AnnouncementTarget;
  isActive?: boolean;
}) {
  return useQuery({
    queryKey: ['announcements', params],
    queryFn: () => apiClient.announcements.list(params),
  });
}

export function useActiveAnnouncements(params?: {
  targetAudience?: AnnouncementTarget;
  programId?: string;
}) {
  return useQuery({
    queryKey: ['announcements', 'active', params],
    queryFn: () => apiClient.announcements.listActive(params),
    refetchInterval: 1000 * 60 * 2, // 2 minutes
  });
}

export function useAnnouncement(id: string) {
  return useQuery({
    queryKey: ['announcements', id],
    queryFn: () => apiClient.announcements.get(id),
    enabled: !!id,
  });
}

export function useCreateAnnouncement() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateAnnouncementInput) => apiClient.announcements.create(data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['announcements'] });
    },
  });
}

export function useUpdateAnnouncement() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateAnnouncementInput }) =>
      apiClient.announcements.update(id, data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['announcements'] });
    },
  });
}

export function useDeleteAnnouncement() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiClient.announcements.delete(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['announcements'] });
    },
  });
}

export function useToggleAnnouncement() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiClient.announcements.toggleActive(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['announcements'] });
    },
  });
}
